import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import type { Institucion } from '@/lib/api';

interface Props {
  instituciones?: Institucion[];
  cats: Array<{ slug: string; label: string; icon: string; color: string }>;
}

export default function Topbar({ instituciones = [], cats }: Props) {
  const { user, logout } = useAuthStore();
  const { section, catActiva, isDark, rolMenuOpen, userMenuOpen, toggleRolMenu, toggleUserMenu, filtrarCat, setInstitucion } = {
    ...useAppStore(),
    setInstitucion: useAuthStore((s) => s.setInstitucion),
  };

  const inst = useAuthStore((s) => s.institucion) ?? instituciones[0] ?? null;
  const [narrow, setNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 720);

  useEffect(() => {
    const handler = () => setNarrow(window.innerWidth < 720);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <div style={{
      position: 'absolute',
      top: narrow ? 14 : 20,
      left: narrow ? 14 : 24,
      right: narrow ? 14 : 24,
      zIndex: 40,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 10,
      animation: 'floatIn 400ms ease-out',
    }}>
      {/* Wordmark */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, order: 1, position: 'relative' }}>
        <div style={{ background: isDark ? 'rgba(10,20,55,0.95)' : 'rgba(255,255,255,0.85)', backdropFilter: 'blur(14px)', borderRadius: 16, padding: '10px 16px', boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(100,150,255,0.18)' : '0 8px 24px rgba(30,58,138,0.12)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="27" stroke="#60A5FA" strokeWidth="3"/><circle cx="24" cy="24" r="3" fill="#60A5FA"/></svg>
          <span style={{ fontWeight: 800, fontSize: 13, color: isDark ? '#93c5fd' : '#1D4ED8', letterSpacing: '-0.01em' }}>ComuniData</span>
        </div>

      </div>

      <div style={{ flex: 1, order: 2 }} />

      {/* Right: bell + avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, order: 3 }}>
        <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(14px)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#1D4ED8', boxShadow: '0 8px 24px rgba(30,58,138,0.12)', flexShrink: 0 }}>
          <i className="ti ti-bell" />
        </div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={toggleUserMenu}
            style={{ width: 38, height: 38, borderRadius: '50%', background: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff', boxShadow: '0 8px 24px rgba(30,58,138,0.2)', flexShrink: 0 }}
          >
            {(user?.nombre ?? 'U').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
          </button>
          {userMenuOpen && (
            <div style={{ position: 'absolute', top: 48, right: 0, width: 190, background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(16px)', borderRadius: 14, padding: 6, boxShadow: '0 16px 36px rgba(30,58,138,0.2)', animation: 'popIn 180ms ease-out', zIndex: 60 }}>
              <div style={{ padding: '10px 12px', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0A1628' }}>{user?.nombre}</div>
                <div style={{ fontSize: 9.5, color: 'rgba(0,0,0,0.4)', marginTop: 1 }}>{inst?.nombre}</div>
              </div>
              <button
                onClick={logout}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', borderRadius: 10, width: '100%', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#dc2626', marginTop: 4 }}
              >
                <i className="ti ti-logout" style={{ fontSize: 13 }} />Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Category pills — only on Panel */}
      {section === 'panel' && (
        <div style={{ order: 4, flex: '1 1 100%', display: 'flex', gap: 8, overflowX: 'auto', padding: '2px 0' }}>
          {cats.map((c) => {
            const active = catActiva === c.slug;
            const pillBg = active
              ? c.color
              : isDark
                ? 'rgba(12,22,55,0.92)'
                : 'rgba(255,255,255,0.72)';
            const pillColor = active ? '#fff' : isDark ? '#c8d8f8' : c.color;
            const pillBorder = isDark && !active ? '1px solid rgba(100,150,255,0.2)' : '1px solid transparent';
            return (
              <button
                key={c.slug}
                onClick={() => filtrarCat(c.slug)}
                style={{
                  background: pillBg, backdropFilter: 'blur(12px)', borderRadius: 20,
                  padding: '9px 16px', fontSize: 12, fontWeight: 700, color: pillColor,
                  boxShadow: active ? `0 4px 14px ${c.color}55` : isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(30,58,138,0.1)',
                  whiteSpace: 'nowrap', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
                  border: pillBorder, transition: 'background 200ms, color 200ms',
                }}
              >
                <i className={`ti ${c.icon}`} style={{ fontSize: 13 }} />{c.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
